import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { AnonymousLayoutComponent } from 'src/app/anonymous-layout/anonymous-layout.component';
import { IsAuthenticatedGuard } from 'src/app/_shared/guards/route.guards';
import { TemporaryMaintenanceComponent } from './temporary-maintenance/temporary-maintenance.component';
import { AppShellComponent } from 'src/app/app-shell/app-shell.component';
import { SalesOpsGuard } from 'src/app/_shared/guards/sales-ops.guard';
import { NotSalesOpsGuard } from 'src/app/_shared/guards/not-sales-ops.guard';
import { SysAdminGuard } from 'src/app/_shared/guards/sysadmin.guard';

let routes: Routes;

const Maintenance = localStorage.getItem('isAppInMaintenance') === 'true';

if (Maintenance) {
  routes = [
    {
      path: '**',
      redirectTo: '',
    },
    {
      path: '',
      component: TemporaryMaintenanceComponent,
    },
  ];
} else {
  routes = [
    {
      path: '',
      canActivate: [IsAuthenticatedGuard],
      runGuardsAndResolvers: 'always',
      component: AppShellComponent,
      children: [
        {
          path: '',
          canActivate: [NotSalesOpsGuard],
          runGuardsAndResolvers: 'always',
          children: [
            { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
            {
              path: 'dashboard',
              loadChildren: (): Promise<any> =>
                import('./dashboard/dashboard.routing').then((r) => r.DashboardRoutingModule),
            },
            {
              path: 'charts',
              loadChildren: (): Promise<any> => import('./charts/charts.routing').then((r) => r.ChartsRoutingModule),
            },
            {
              path: 'charts/:mpId/:accountId/:alarmId',
              loadChildren: (): Promise<any> => import('./charts/charts.routing').then((r) => r.ChartsRoutingModule),
            },
            {
              path: 'energy-power',
              loadChildren: (): Promise<any> =>
                import('./energy-power/energy-power.routing').then((r) => r.EnergyPowerRoutingModule),
            },
            {
              path: 'hf-emissions',
              loadChildren: (): Promise<any> =>
                import('./hf-emissions/hf-emissions.routing').then((r) => r.HfEmissionsRoutingModule),
            },
            {
              path: 'reports',
              loadChildren: (): Promise<any> =>
                import('./reports-recommendations/reports-recommendations.routing').then(
                  (r) => r.ReportsRecommendationRoutingModule
                ),
            },
            {
              path: 'report-writer',
              loadChildren: (): Promise<any> =>
                import('./report-writer/report-writer.routing').then((r) => r.ReportWriterRoutingModule),
            },
            {
              path: 'monthly-report/:accountId/:mpId/:year/:month',
              redirectTo: '/reports/monthly/:accountId/:mpId/:year/:month',
            },
            {
              path: 'fleet',
              loadChildren: (): Promise<any> =>
                import('./fleet-overview/fleet-overview.routing').then((r) => r.FleetOverviewRoutingModule),
            },
            {
              path: 'event-graph',
              loadChildren: (): Promise<any> =>
                import('./event-graph/event-graph.routing').then((r) => r.EventGraphRoutingModule),
            },
            {
              path: 'trend-anomaly',
              loadChildren: (): Promise<any> =>
                import('./trend-anomaly/trend-anomaly.routing').then((r) => r.TrendAnomalyRoutingModule),
            },
            {
              path: 'events-list',
              loadChildren: (): Promise<any> =>
                import('./events-list/events-list.routing').then((r) => r.EventsListRoutingModule),
            },
            {
              path: 'more-information',
              loadChildren: (): Promise<any> =>
                import('./information/information.routing').then((r) => r.InformationRoutingModule),
            },
            {
              path: 'profile',
              loadChildren: (): Promise<any> => import('./profile/profile.routing').then((r) => r.ProfileRoutingModule),
            },
            {
              path: 'manage-members',
              loadChildren: (): Promise<any> =>
                import('./manage-members/manage-members.routing').then((r) => r.ManageMembersRoutingModule),
            },
            {
              path: 'manage-subscription',
              loadChildren: (): Promise<any> =>
                import('./manage-subscription/manage-subscription.routing').then(
                  (r) => r.ManageSubscriptionRoutingModule
                ),
            },
            {
              path: 'admin',
              loadChildren: (): Promise<any> =>
                import('./sysadmin/sysadmin.routing').then((r) => r.SysAdminRoutingModule),
            },
            {
              path: 'system-admin',
              canActivate: [SysAdminGuard],
              runGuardsAndResolvers: 'always',
              loadChildren: (): Promise<any> =>
                import('./sysadmin-tool/sysadmin-tool.routing').then((r) => r.SysadminToolRoutingModule),
            },
            {
              path: 'notification',
              loadChildren: (): Promise<any> =>
                import('./notification/notification.routing').then((r) => r.NotificationRoutingModule),
            },
            {
              path: 'rms-variation-analysis',
              loadChildren: (): Promise<any> =>
                import('./rms-variation-analysis/rms-variation-analysis.routing').then(
                  (r) => r.RmsVariationAnalysisRoutingModule
                ),
            },
            {
              path: 'maintenance',
              loadChildren: (): Promise<any> =>
                import('./maintenance/maintenance.routing').then((r) => r.MaintenanceRoutingModule),
            },
            {
              path: 'sales-ops',
              canActivate: [SalesOpsGuard],
              runGuardsAndResolvers: 'always',
              loadChildren: (): Promise<any> =>
                import('./sales-ops/sales-ops.routing').then((r) => r.SalesOpsRoutingModule),
            },
          ],
        },
        {
          path: '',
          canActivate: [SalesOpsGuard],
          runGuardsAndResolvers: 'always',
          children: [
            { path: '', redirectTo: '/sales-ops', pathMatch: 'full' },
            {
              path: 'sales-ops',
              loadChildren: (): Promise<any> =>
                import('./sales-ops/sales-ops.routing').then((r) => r.SalesOpsRoutingModule),
            },
          ],
        },
      ],
    },

    {
      path: '',
      component: AnonymousLayoutComponent,
      children: [
        { path: 'passwordRecovery', redirectTo: 'login/password-recovery' },
        { path: 'password-recovery', redirectTo: 'login/password-recovery' },
        {
          path: 'login',
          loadChildren: (): Promise<any> =>
            import('./qubescan-login/qubescan-login.routing').then((r) => r.QubescanLoginRoutingModule),
        },
        {
          path: 'drivescan',
          loadChildren: (): Promise<any> => import('./login/login.routing').then((r) => r.LoginRoutingModule),
        },
        {
          path: 'qubescan',
          redirectTo: 'login',
        },
        {
          path: 'register',
          loadChildren: (): Promise<any> =>
            import('./activation/activation.routing').then((r) => r.ActivationRoutingModule),
        },
      ],
    },
  ];
}
@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
